const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

// スライディングセッション: バックエンドは認証成功のたびに新しい期限の
// トークンを X-Refresh-Token ヘッダで返す。全 fetch をここで一度だけラップし、
// そのヘッダがあれば localStorage のトークンを差し替える。これで「最終
// アクセスから N 分」のアイドルタイムアウトになり、操作中はセッションが
// 切れない (各 fetch 呼び出し側の修正は不要)。
if (typeof window !== 'undefined' && !window.__authFetchWrapped) {
  window.__authFetchWrapped = true;
  const _origFetch = window.fetch.bind(window);
  window.fetch = async (...args) => {
    const res = await _origFetch(...args);
    try {
      const refreshed = res.headers && res.headers.get && res.headers.get('X-Refresh-Token');
      if (refreshed) {
        localStorage.setItem('token', refreshed);
      }
    } catch { /* ignore — token refresh は best-effort */ }
    return res;
  };
}

// FastAPI/Pydantic のエラーレスポンスから人間可読なメッセージを抽出する
// detail が配列（422 validation エラー）の場合に [object Object] にならないようにする
const parseErrorDetail = (errorData, status) => {
  if (!errorData) return `HTTP error! status: ${status}`;
  const d = errorData.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    return d.map(item => {
      const loc = Array.isArray(item.loc) ? item.loc.filter(p => p !== 'body').join('.') : '';
      return loc ? `${loc}: ${item.msg}` : item.msg;
    }).filter(Boolean).join('; ') || `HTTP error! status: ${status}`;
  }
  // 構造を持たせた detail は message を見せる。JSON をそのまま出すと
  // 画面に {"reason":...} が並んで読めない。
  if (d && typeof d === 'object' && typeof d.message === 'string') return d.message;
  if (d && typeof d === 'object') return JSON.stringify(d);
  return `HTTP error! status: ${status}`;
};

// 認証ヘッダーを取得するヘルパー関数
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

// Handle 401 (expired JWT) consistently: drop the stale token and bounce
// the user to /login with a clearer message. Returning null lets callers
// short-circuit any further parsing.
let _redirectingToLogin = false;
const handleUnauthorized = () => {
  try { localStorage.removeItem('token'); } catch { /* ignore */ }
  if (_redirectingToLogin) return;
  _redirectingToLogin = true;
  // Defer slightly so any in-flight UI work can flush.
  setTimeout(() => {
    // Avoid clobbering the user's current page if they're already on /login.
    if (!window.location.pathname.startsWith('/login')) {
      // eslint-disable-next-line no-alert
      window.alert('セッションが切れました。再度ログインしてください。');
      window.location.href = '/login';
    }
  }, 0);
};

export const getLatestCrawlingStatus = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/status/latest_status`, {
      headers: getAuthHeaders(), // 追加
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching latest crawling status:", error);
    return null;
  }
};

export const getKikansStatus = async (page = 1, limit = 20, filters = {}, sort = {}) => {
  try {
    let url = `${API_BASE_URL}/status/kikans_status?page=${page}&limit=${limit}`;
    if (filters.kikan_code) url += `&kikan_code=${encodeURIComponent(filters.kikan_code)}`;
    if (filters.kikan_name) url += `&kikan_name=${encodeURIComponent(filters.kikan_name)}`;
    if (filters.has_crawl !== undefined && filters.has_crawl !== '') url += `&has_crawl=${filters.has_crawl}`;
    if (filters.last_crawl_after) url += `&last_crawl_after=${encodeURIComponent(filters.last_crawl_after)}`;
    
    if (sort.key) url += `&sort_by=${sort.key}`;
    if (sort.order) url += `&order=${sort.order}`;

    const response = await fetch(url, {
      headers: getAuthHeaders(), // 追加
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching kikans status:", error);
    return null;
  }
};

export const startCrawl = async (kikanCode, options = {}) => {
  // options: { concurrency, domain_gap_seconds, loop, loop_wait_seconds }
  // 未指定のフィールドはバックエンドのデフォルト (concurrency=5, gap=2s, loop=false) が使われる。
  const body = { kikan_code: kikanCode };
  if (options.concurrency !== undefined) body.concurrency = options.concurrency;
  if (options.domain_gap_seconds !== undefined) body.domain_gap_seconds = options.domain_gap_seconds;
  if (options.loop !== undefined) body.loop = options.loop;
  if (options.loop_wait_seconds !== undefined) body.loop_wait_seconds = options.loop_wait_seconds;
  if (options.skip_recent_days !== undefined) body.skip_recent_days = options.skip_recent_days;

  try {
    const response = await fetch(`${API_BASE_URL}/crawl/start`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error starting crawl:", error);
    throw error;
  }
};

export const stopCrawlLoop = async (kikanCode) => {
  // Loopフラグ付きで開始されたクロールの「次のイテレーション」を止める。
  // 現在実行中のイテレーションは完走する (即時停止は stopCrawl(process_id) を使う)。
  const response = await fetch(`${API_BASE_URL}/crawl/loop/stop`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ kikan_code: kikanCode }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `HTTP ${response.status}`);
  }
  return response.json();
};

export const stopCrawl = async (processId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/crawl/stop`, {
      method: 'POST',
      headers: getAuthHeaders(), // 修正
      body: JSON.stringify({ process_id: processId }),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error stopping crawl:", error);
    throw error;
  }
};

export const getSchedulerDailyLimit = async () => {
  const response = await fetch(`${API_BASE_URL}/system/scheduler/limit`, { headers: getAuthHeaders() });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return await response.json();
};

export const setSchedulerDailyLimit = async (dailyUrlLimit) => {
  const response = await fetch(`${API_BASE_URL}/system/scheduler/limit`, {
    method: 'PUT',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ daily_url_limit: dailyUrlLimit }),
  });
  if (!response.ok) {
    const e = await response.json().catch(() => ({}));
    throw new Error(e.detail || `HTTP ${response.status}`);
  }
  return await response.json();
};

export const getSchedulerToday = async () => {
  const response = await fetch(`${API_BASE_URL}/system/scheduler/today`, { headers: getAuthHeaders() });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return await response.json();
};

export const getSchedulerForecast = async (days = 7) => {
  const response = await fetch(`${API_BASE_URL}/system/scheduler/forecast?days=${days}`, { headers: getAuthHeaders() });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return await response.json();
};

// 調査対象に「含める役職」ホワイトリスト (LDB更新候補の役職フィルタ)。
export const getResearcherRoles = async () => {
  const response = await fetch(`${API_BASE_URL}/system/researcher-roles`, { headers: getAuthHeaders() });
  if (response.status === 401) { handleUnauthorized(); return null; }
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return await response.json();
};

export const setResearcherRoles = async (includeRoles) => {
  const response = await fetch(`${API_BASE_URL}/system/researcher-roles`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify({ include_roles: includeRoles }),
  });
  if (response.status === 401) { handleUnauthorized(); throw new Error('セッションが切れています。再ログインしてください。'); }
  if (!response.ok) {
    const e = await response.json().catch(() => ({}));
    throw new Error(e.detail || `HTTP ${response.status}`);
  }
  return await response.json();
};

// 機関分類 (大学/国立研究機関/企業/その他) ごとの include 役職を保存 (要対応 #24)。
export const setResearcherRolesByClass = async (byClass) => {
  const response = await fetch(`${API_BASE_URL}/system/researcher-roles`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify({ by_class: byClass }),
  });
  if (response.status === 401) { handleUnauthorized(); throw new Error('セッションが切れています。再ログインしてください。'); }
  if (!response.ok) {
    const e = await response.json().catch(() => ({}));
    throw new Error(e.detail || `HTTP ${response.status}`);
  }
  return await response.json();
};

// mode: 'resume' (未解析のみ) | 'full' (ANALYZED→CRAWLEDで全件再解析)
export const triggerAnalysis = async (processId, mode = 'resume') => {
  try {
    const response = await fetch(`${API_BASE_URL}/crawl/${processId}/analyze?mode=${encodeURIComponent(mode)}`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error triggering analysis:", error);
    throw error;
  }
};

export const approveUpdateAsTransfer = async (tmpId, targetKennkyuusyaCode) => {
  const response = await fetch(`${API_BASE_URL}/updates/pending_updates/${tmpId}/approve_as_transfer`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ target_kennkyuusya_code: targetKennkyuusyaCode }),
  });
  if (response.status === 401) {
    handleUnauthorized();
    throw new Error('セッションが切れています。再ログインしてください。');
  }
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(parseErrorDetail(errorData, response.status));
  }
  return await response.json();
};

export const getPendingUpdateCounts = async (processId = null) => {
  try {
    const url = `${API_BASE_URL}/updates/pending_updates/counts${processId ? `?process_id=${processId}` : ''}`;
    const response = await fetch(url, { headers: getAuthHeaders() });
    if (response.status === 401) { handleUnauthorized(); return null; }
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (e) {
    console.error("Error fetching pending update counts:", e);
    return null;
  }
};

export const getPendingUpdatePreview = async (tmpId) => {
  const response = await fetch(`${API_BASE_URL}/updates/pending_updates/${tmpId}/preview`, {
    headers: getAuthHeaders(),
  });
  if (response.status === 401) {
    handleUnauthorized();
    throw new Error('セッションが切れています。再ログインしてください。');
  }
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(parseErrorDetail(errorData, response.status));
  }
  return await response.json();
};

export const getPendingUpdates = async (
  table = null,
  processId = null,
  kikanKeyword = null,
  page = 1,
  limit = 3,
  recommendation = null,
  hideSkip = false,
) => {
  try {
    // table=null → サーバ側で ORGANIZATION + RESEARCHER 両方を返す (タブ廃止後の既定)
    // table='researcher' or 'organization' → 後方互換のため片方だけ取得
    let targetType = null;
    if (table === 'researcher') targetType = 'RESEARCHER';
    else if (table === 'organization') targetType = 'ORGANIZATION';

    const params = new URLSearchParams();
    if (targetType) params.set('target_type', targetType);
    params.set('page', String(page));
    params.set('limit', String(limit));
    if (processId) params.set('process_id', String(processId));
    if (kikanKeyword) params.set('kikan_keyword', kikanKeyword);
    if (recommendation) params.set('recommendation', recommendation);
    if (hideSkip) params.set('hide_skip', 'true');

    const url = `${API_BASE_URL}/updates/pending_updates?${params.toString()}`;
    const response = await fetch(url, {
      headers: getAuthHeaders(),
    });
    if (response.status === 401) {
      handleUnauthorized();
      throw new Error('セッションが切れています。再ログインしてください。');
    }
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching pending updates:", error);
    return null;
  }
};

// Schedule API
export const getSchedules = async (filters = {}) => {
  try {
    let url = `${API_BASE_URL}/schedules/?`;
    if (filters.start_date) url += `&start_date=${filters.start_date}`;
    if (filters.end_date) url += `&end_date=${filters.end_date}`;
    if (filters.kikan_code) url += `&kikan_code=${filters.kikan_code}`;
    if (filters.status) url += `&status=${filters.status}`;

    const response = await fetch(url, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error("Error fetching schedules:", error);
    return [];
  }
};

export const createSchedule = async (data) => {
  try {
    const response = await fetch(`${API_BASE_URL}/schedules/`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.detail || `HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error creating schedule:", error);
    throw error;
  }
};

export const updateSchedule = async (id, data) => {
  try {
    const response = await fetch(`${API_BASE_URL}/schedules/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.detail || `HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error updating schedule:", error);
    throw error;
  }
};

export const deleteSchedule = async (id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/schedules/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || `HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error deleting schedule:", error);
    throw error;
  }
};

export const generateSchedules = async (params) => {
  try {
    const response = await fetch(`${API_BASE_URL}/schedules/generate`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(params),
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.detail || `HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error generating schedules:", error);
    throw error;
  }
};

export const getScheduleSummary = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/schedules/summary`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error("Error fetching schedule summary:", error);
    return null;
  }
};

// forceNew: 同姓同名の既存研究者がいる「新規」を、別人と確かめたうえで新規登録する
// ときだけ true (KAKEN 第 1 段・二重登録の防止)。既定ではサーバが 409 で止める。
export const approveUpdate = async (id, tableName, approved, { forceNew = false } = {}) => {
  try {
    const response = await fetch(`${API_BASE_URL}/updates/approve`, {
      method: 'POST',
      headers: getAuthHeaders(), // 修正
      body: JSON.stringify({ id, approved, force_new: forceNew }),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error approving update:", error);
    throw error;
  }
};

// /compare インライン承認用: 1 機関の PENDING 差分を全件取得し、sosiki_code 単位で
// 扱えるよう data 配列を返す。/pending_updates をページ送りで全グループ集約 (推奨/
// 同姓同名/effective_change_type 等の判定は既存ロジックそのまま＝見たもの=承認するもの)。
export const getAllPendingUpdatesForKikan = async (kikanCode) => {
  const PER_PAGE = 50; // (kikan,sosiki) グループ/頁。サーバ上限。
  const all = [];
  for (let page = 1; page <= 200; page++) {
    const params = new URLSearchParams();
    params.set('kikan_keyword', kikanCode); // exact kikan_code もマッチ
    params.set('page', String(page));
    params.set('limit', String(PER_PAGE));
    const response = await fetch(`${API_BASE_URL}/updates/pending_updates?${params.toString()}`, {
      headers: getAuthHeaders(),
    });
    if (response.status === 401) { handleUnauthorized(); throw new Error('セッションが切れています。再ログインしてください。'); }
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const json = await response.json();
    const rows = (json && json.data) || [];
    all.push(...rows);
    if (rows.length === 0) break;
    const total = (json && json.total_count) || 0;
    if (page * PER_PAGE >= total) break;
  }
  return all;
};

// V2 P3 研究者補正: PENDING の研究者行の抽出値 (氏名/カナ/役職/メール) を
// 承認前に編集する。元の抽出値は edited_fields に保持される。
export const editPendingUpdate = async (tmpId, fields) => {
  const response = await fetch(`${API_BASE_URL}/updates/pending_updates/${tmpId}/edit`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(fields),
  });
  if (response.status === 401) { handleUnauthorized(); throw new Error('セッションが切れています。再ログインしてください。'); }
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(parseErrorDetail(errorData, response.status));
  }
  return await response.json();
};

// --- 研究者重複の棚卸 (V2 P5) ------------------------------------------------
export const dedupScan = async ({ kikanCode = null, includeMarked = false, limit = 50, offset = 0 } = {}) => {
  const params = new URLSearchParams();
  if (kikanCode) params.set('kikan_code', kikanCode);
  if (includeMarked) params.set('include_marked', 'true');
  params.set('limit', String(limit));
  params.set('offset', String(offset));
  const response = await fetch(`${API_BASE_URL}/dedup/scan?${params.toString()}`, {
    headers: getAuthHeaders(),
  });
  if (response.status === 401) { handleUnauthorized(); throw new Error('セッションが切れています。再ログインしてください。'); }
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return await response.json();
};

export const dedupMark = async ({ code_a, code_b, note = null }) => {
  const response = await fetch(`${API_BASE_URL}/dedup/mark`, {
    method: 'POST', headers: getAuthHeaders(),
    body: JSON.stringify({ code_a, code_b, note }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(parseErrorDetail(errorData, response.status));
  }
  return await response.json();
};

// V2 P5後半: 研究者マスタの統合 (duplicate→canonical。所属付け替え+SOFT_DELETE)。
export const dedupMerge = async ({ canonical_code, duplicate_code }) => {
  const response = await fetch(`${API_BASE_URL}/dedup/merge`, {
    method: 'POST', headers: getAuthHeaders(),
    body: JSON.stringify({ canonical_code, duplicate_code }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(parseErrorDetail(errorData, response.status));
  }
  return await response.json();
};

export const dedupUnmark = async ({ code_a, code_b }) => {
  const response = await fetch(`${API_BASE_URL}/dedup/unmark`, {
    method: 'POST', headers: getAuthHeaders(),
    body: JSON.stringify({ code_a, code_b }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(parseErrorDetail(errorData, response.status));
  }
  return await response.json();
};

export const approveUpdatesBulk = async (ids, approved) => {
  const response = await fetch(`${API_BASE_URL}/updates/approve_bulk`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ ids, approved }),
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Bulk approval failed');
  }
  return response.json();
};

export const getCrawlDetail = async (processId, status = null) => {
  try {
    let url = `${API_BASE_URL}/status/crawl_detail/${processId}`;
    if (status) {
      url += `?status=${status}`;
    }
    const response = await fetch(url, {
      headers: getAuthHeaders(), // 追加
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching crawl detail:", error);
    return null;
  }
};

export const getAllCrawlErrors = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/status/all_errors`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching all crawl errors:", error);
    return null;
  }
};

export const fixOrganizationUrl = async (processId, sosikiCode, newUrl) => {
  try {
    const response = await fetch(`${API_BASE_URL}/corrections/fix_url`, {
      method: 'POST',
      headers: getAuthHeaders(), // 修正
      body: JSON.stringify({ process_id: processId, sosiki_code: sosikiCode, new_url: newUrl }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(parseErrorDetail(errorData, response.status));
    }
    return await response.json();
  } catch (error) {
    console.error("Error fixing URL:", error);
    throw error;
  }
};

export const markOrganizationDeleted = async (processId, sosikiCode, reason) => {
  try {
    const response = await fetch(`${API_BASE_URL}/corrections/mark_deleted`, {
      method: 'POST',
      headers: getAuthHeaders(), // 修正
      body: JSON.stringify({ process_id: processId, sosiki_code: sosikiCode, reason: parseInt(reason) }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(parseErrorDetail(errorData, response.status));
    }
    return await response.json();
  } catch (error) {
    console.error("Error marking deleted:", error);
    throw error;
  }
};

// Auto-fix endpoints. Preview returns suggested candidates per error row;
// Apply commits the ones whose confidence meets `min_confidence`.
export const previewUrlAutoFix = async ({ error_ids = null, process_id = null, enable_content_validation = true } = {}) => {
  const response = await fetch(`${API_BASE_URL}/corrections/auto_fix/preview`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ error_ids, process_id, enable_content_validation }),
  });
  if (response.status === 401) { handleUnauthorized(); throw new Error('セッションが切れています'); }
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(parseErrorDetail(errorData, response.status));
  }
  return await response.json();
};

// Streaming variant of preview. Calls the NDJSON endpoint and invokes
// `onEvent(evt)` for each parsed event ({event:'start'|'row_start'|'row_done'
// |'row_error'|'complete', ...}). Returns when the stream ends. Allows the UI
// to show real-time progress instead of waiting 20+ minutes for the whole
// batch to finish.
export const previewUrlAutoFixStream = async ({ error_ids = null, process_id = null, enable_content_validation = true, onEvent } = {}) => {
  const response = await fetch(`${API_BASE_URL}/corrections/auto_fix/preview/stream`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ error_ids, process_id, enable_content_validation }),
  });
  if (response.status === 401) { handleUnauthorized(); throw new Error('セッションが切れています'); }
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(parseErrorDetail(errorData, response.status));
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  // Read the stream chunk-by-chunk, splitting on newline. Each non-empty line
  // is a JSON event the server emitted with `... + "\n"`.
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop();  // last partial line goes back into the buffer
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      try {
        const evt = JSON.parse(trimmed);
        if (onEvent) onEvent(evt);
      } catch (e) {
        console.warn('auto_fix stream: failed to parse line', trimmed, e);
      }
    }
  }
  // Flush any remaining bytes.
  const tail = buffer.trim();
  if (tail) {
    try { if (onEvent) onEvent(JSON.parse(tail)); } catch { /* noop */ }
  }
};

export const applyUrlAutoFix = async ({ items, min_confidence = 'HIGH' }) => {
  const response = await fetch(`${API_BASE_URL}/corrections/auto_fix/apply`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ items, min_confidence }),
  });
  if (response.status === 401) { handleUnauthorized(); throw new Error('セッションが切れています'); }
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(parseErrorDetail(errorData, response.status));
  }
  return await response.json();
};

export const fixResearcherUrl = async (sosikiCode, oldUrl, newUrl) => {
 try {
   const response = await fetch(`${API_BASE_URL}/corrections/fix_researcher_url`, {
     method: 'POST',
     headers: getAuthHeaders(),
     body: JSON.stringify({ sosiki_code: sosikiCode, old_url: oldUrl, new_url: newUrl }),
   });
   if (!response.ok) {
     const errorData = await response.json().catch(() => null);
     throw new Error(parseErrorDetail(errorData, response.status));
   }
   return await response.json();
 } catch (error) {
   console.error("Error fixing researcher URL:", error);
   throw error;
 }
};

export const getCrawlResults = async (page = 1, limit = 50, filters = {}) => {
  try {
    let url = `${API_BASE_URL}/updates/results?page=${page}&limit=${limit}`;
    if (filters.process_id) url += `&process_id=${filters.process_id}`;
    if (filters.kikan_code) url += `&kikan_code=${filters.kikan_code}`;
    if (filters.approval_status) url += `&approval_status=${filters.approval_status}`;
    if (filters.target_type) url += `&target_type=${filters.target_type}`; // 追加
    if (filters.change_type) url += `&change_type=${filters.change_type}`; // 追加
    if (filters.labo_flg !== undefined && filters.labo_flg !== '') url += `&labo_flg=${filters.labo_flg}`; // 追加

    const response = await fetch(url, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching crawl results:", error);
    return null;
  }
};

export const getCrawlResultSummary = async (processId = null) => {
  try {
    let url = `${API_BASE_URL}/updates/results/summary`;
    if (processId) {
      url += `?process_id=${processId}`;
    }

    const response = await fetch(url, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching crawl result summary:", error);
    return null;
  }
};

// Per-kikan triage board (quality signals + approval work queue).
// Pass {includeUncrawled: true} to also list mt_kikan rows that have
// never been crawled (they show up with null process / 0 counts).
export const getCrawlResultsBoard = async (opts = {}) => {
  try {
    const params = new URLSearchParams();
    if (opts.includeUncrawled) params.set('include_uncrawled', '1');
    if (opts.fresh) params.set('fresh', '1');  // キャッシュ無視 (承認操作後/明示リロード)
    if (opts.kikanCode) params.set('kikan_code', opts.kikanCode);  // 指定時は当該機関のみ集計 (高速)
    const qs = params.toString() ? `?${params}` : '';
    const response = await fetch(`${API_BASE_URL}/updates/results/board${qs}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching crawl results board:", error);
    return null;
  }
};

// === Researcher-list URL discovery (抽出激減対策) ============================
// Surfaces the post-crawl "did we miss a 教員一覧 page?" workflow.

export const getDiscoveryCandidates = async ({ kikan_code, sosiki_code, status = 'CANDIDATE', limit = 500 } = {}) => {
  const qs = new URLSearchParams();
  if (kikan_code) qs.set('kikan_code', kikan_code);
  if (sosiki_code) qs.set('sosiki_code', sosiki_code);
  if (status) qs.set('status', status);
  if (limit) qs.set('limit', String(limit));
  const response = await fetch(`${API_BASE_URL}/discovery/candidates?${qs.toString()}`, { headers: getAuthHeaders() });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return await response.json();
};

export const getDiscoverySummary = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/discovery/summary`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return await response.json();
  } catch (e) {
    console.error('Error fetching discovery summary:', e);
    return { data: [] };
  }
};

export const startDiscovery = async ({ process_id, kikan_code, sosiki_codes, top_n_per_sosiki = 3 } = {}) => {
  const response = await fetch(`${API_BASE_URL}/discovery/start`, {
    method: 'POST',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ process_id, kikan_code, sosiki_codes, top_n_per_sosiki }),
  });
  if (!response.ok) {
    const txt = await response.text().catch(() => '');
    throw new Error(`HTTP ${response.status}: ${txt}`);
  }
  return await response.json();
};

export const applyDiscoveryCandidate = async (id, note) => {
  const response = await fetch(`${API_BASE_URL}/discovery/apply`, {
    method: 'POST',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ id, note }),
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return await response.json();
};

export const dismissDiscoveryCandidate = async (id, note) => {
  const response = await fetch(`${API_BASE_URL}/discovery/dismiss`, {
    method: 'POST',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ id, note }),
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return await response.json();
};

// === Org-tree investigation (URL探索→LLM→組織図) =============================
// Phase 2-4 of the URL discovery initiative.

export const startOrgTreeInvestigation = async ({
  kikan_code, max_depth = 3, max_urls, seed_url, llm_mode = 'off',
} = {}) => {
  // max_urls 未指定なら backend が機関規模 (LDB 組織数+研究者数) から自動算出
  const body = { kikan_code, max_depth, seed_url, llm_mode };
  if (max_urls && max_urls > 0) body.max_urls = max_urls;
  const response = await fetch(`${API_BASE_URL}/org_tree/investigate`, {
    method: 'POST',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const txt = await response.text().catch(() => '');
    throw new Error(`HTTP ${response.status}: ${txt}`);
  }
  return await response.json();
};

// Phase 5e Process B: visit 済の URL に対して LLM を後追い実行。
export const analyzeOrgTreeRun = async (runId, { only_urls, include_already_classified = false } = {}) => {
  const response = await fetch(`${API_BASE_URL}/org_tree/runs/${runId}/analyze`, {
    method: 'POST',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ only_urls, include_already_classified }),
  });
  if (!response.ok) {
    const txt = await response.text().catch(() => '');
    throw new Error(`HTTP ${response.status}: ${txt}`);
  }
  return await response.json();
};

export const getOrgTreeRuns = async ({ kikan_code, limit = 50 } = {}) => {
  const qs = new URLSearchParams();
  if (kikan_code) qs.set('kikan_code', kikan_code);
  qs.set('limit', String(limit));
  const response = await fetch(`${API_BASE_URL}/org_tree/runs?${qs}`, { headers: getAuthHeaders() });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return await response.json();
};

export const getOrgTreeRun = async (runId) => {
  const response = await fetch(`${API_BASE_URL}/org_tree/runs/${runId}`, { headers: getAuthHeaders() });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return await response.json();
};

export const getOrgTreeVisits = async (runId, { status, depth, q, urlType, limit = 500 } = {}) => {
  const qs = new URLSearchParams();
  if (status) qs.set('status', status);
  if (depth !== undefined) qs.set('depth', String(depth));
  if (q && q.trim()) qs.set('q', q.trim());
  if (urlType) qs.set('url_type', urlType);
  qs.set('limit', String(limit));
  const response = await fetch(`${API_BASE_URL}/org_tree/runs/${runId}/visits?${qs}`, { headers: getAuthHeaders() });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return await response.json();
};

export const stopOrgTreeRun = async (runId) => {
  const response = await fetch(`${API_BASE_URL}/org_tree/runs/${runId}/stop`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return await response.json();
};

// 上限到達 (frontier保持) のとき max_urls を +additional して続きを再開。
export const extendOrgTreeRun = async (runId, additional = 5000) => {
  const response = await fetch(
    `${API_BASE_URL}/org_tree/runs/${runId}/extend?additional=${additional}`,
    { method: 'POST', headers: getAuthHeaders() }
  );
  if (!response.ok) {
    const txt = await response.text().catch(() => '');
    throw new Error(`HTTP ${response.status}: ${txt}`);
  }
  return await response.json();
};

// Phase 5o: クロール済 HTML から ¥0 で機械抽出ツリーを構築。
// breadcrumb / JSON-LD / title 区切りだけで親子関係を抽出する。
export const buildOrgTreeFromMechanical = async (runId) => {
  const response = await fetch(`${API_BASE_URL}/org_tree/runs/${runId}/build_from_mechanical`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    const txt = await response.text().catch(() => '');
    throw new Error(`HTTP ${response.status}: ${txt}`);
  }
  return await response.json();
};

// Phase 5o: 機械抽出した組織候補を LLM で 1 回検証・整理 (~¥30)。
export const refineOrgTreeWithLLM = async (runId) => {
  const response = await fetch(`${API_BASE_URL}/org_tree/runs/${runId}/refine_with_llm`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    const txt = await response.text().catch(() => '');
    throw new Error(`HTTP ${response.status}: ${txt}`);
  }
  return await response.json();
};

// Phase 5q: soran 等から研究者・役職・所属を機械抽出 (¥0)。新組織も発見。
export const extractResearchers = async (runId) => {
  const response = await fetch(`${API_BASE_URL}/org_tree/runs/${runId}/extract_researchers`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    const txt = await response.text().catch(() => '');
    throw new Error(`HTTP ${response.status}: ${txt}`);
  }
  return await response.json();
};

// LLM(C): 教員一覧ページを Gemini で解析して研究者抽出。sample を渡すと
// 先頭 N 枚だけ処理 (コスト検証用)。¥が発生する。
export const extractResearchersLLM = async (runId, sample) => {
  const qs = sample ? `?sample=${encodeURIComponent(sample)}` : '';
  const response = await fetch(`${API_BASE_URL}/org_tree/runs/${runId}/extract_researchers_llm${qs}`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    const txt = await response.text().catch(() => '');
    throw new Error(`HTTP ${response.status}: ${txt}`);
  }
  return await response.json();
};

// 抽出方式 (method=llm / mechanical) で研究者を一覧。LLM抽出結果だけを
// 機械ノイズと混ぜずに確認するためのビュー (source_url 順)。
export const getRunResearchers = async (runId, { method, limit = 3000 } = {}) => {
  const qs = new URLSearchParams();
  if (method) qs.set('method', method);
  qs.set('limit', String(limit));
  const response = await fetch(`${API_BASE_URL}/org_tree/runs/${runId}/researchers_list?${qs}`, { headers: getAuthHeaders() });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return await response.json();
};

// 教員一覧と判定したページ (所属組織名・登録状況つき)。/compare のマッチと
// 突合して researcher_urls 登録候補 / sosiki_code 解決率を出す (R1)。
export const getMemberUrlCandidates = async (runId) => {
  const response = await fetch(`${API_BASE_URL}/org_tree/runs/${runId}/member_url_candidates`, { headers: getAuthHeaders() });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return await response.json();
};

// LDB起点 URL監査: 各LDB組織の現URL生死チェック + クロール資産からの
// トップURL/研究者一覧URL逆引きをバックグラウンド実行。
export const triggerUrlAudit = async (kikanCode) => {
  const response = await fetch(`${API_BASE_URL}/org_tree/compare/${encodeURIComponent(kikanCode)}/audit_urls`, {
    method: 'POST', headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return await response.json();
};

export const getUrlAudit = async (kikanCode) => {
  const response = await fetch(`${API_BASE_URL}/org_tree/compare/${encodeURIComponent(kikanCode)}/url_audit`, { headers: getAuthHeaders() });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return await response.json();
};

// 第2段: 直近クロールの研究者差分 (NEW/UPDATE/DELETE) を sosiki_code 単位で取得し、
// LDB組織ツリーの研究者行に重ねて表示する。
export const getResearcherDiffs = async (kikanCode) => {
  const response = await fetch(`${API_BASE_URL}/org_tree/compare/${encodeURIComponent(kikanCode)}/researcher_diffs`, { headers: getAuthHeaders() });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return await response.json();
};

// #81: LDB未登録の研究室候補 (クロール結果の所属表記から抽出) を親組織ごとに取得。
export const getNewOrgCandidates = async (kikanCode) => {
  const response = await fetch(`${API_BASE_URL}/org_tree/compare/${encodeURIComponent(kikanCode)}/new_org_candidates`, { headers: getAuthHeaders() });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return await response.json();
};

// #86: 候補を「不要 (登録しない)」にする / 取り消して戻す。
// 不要にした候補は data から外れ、dismissed 側で返ってくる (完全には消さない)。
const postNewOrgCandidateMark = async (kikanCode, action, parentSosikiCode, name) => {
  const response = await fetch(
    `${API_BASE_URL}/org_tree/compare/${encodeURIComponent(kikanCode)}/new_org_candidates/${action}`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ parent_sosiki_code: parentSosikiCode, name }),
    });
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(parseErrorDetail(errorData, response.status));
  }
  return await response.json();
};

export const dismissNewOrgCandidate = (kikanCode, parentSosikiCode, name) =>
  postNewOrgCandidateMark(kikanCode, 'dismiss', parentSosikiCode, name);

export const restoreNewOrgCandidate = (kikanCode, parentSosikiCode, name) =>
  postNewOrgCandidateMark(kikanCode, 'restore', parentSosikiCode, name);

// ⑤照合 (再クロールなし): 最初の網羅クロール素材から研究者抽出→突合→差分生成。
// sosikiCode 指定時はそのノード配下 (サブツリー) に限定して照合 (V2 P2 組織単位)。
// onlyOrgFixed (#48): 「組織仮確定」済みの組織を起点にまとめて照合する。
export const reconcileFromCorpus = async (kikanCode, sosikiCode = null, onlyOrgFixed = false) => {
  const response = await fetch(`${API_BASE_URL}/org_tree/compare/${encodeURIComponent(kikanCode)}/reconcile`, {
    method: 'POST',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(sosikiCode ? { sosiki_code: sosikiCode }
      : (onlyOrgFixed ? { only_org_fixed: true } : {})),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(parseErrorDetail(errorData, response.status));
  }
  return await response.json();
};

// V2 3組織補正: 組織の親付け替え (D&D/格上げ)。new_parent_sosiki_code=null でトップ階層へ。
export const moveOrg = async (sosikiCode, { kikan_code, new_parent_sosiki_code = null }) => {
  const response = await fetch(`${API_BASE_URL}/org_tree/sosiki/${encodeURIComponent(sosikiCode)}/move`, {
    method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ kikan_code, new_parent_sosiki_code }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(parseErrorDetail(errorData, response.status));
  }
  return await response.json();
};

// V2 #43: 組織の住所・連絡先・旧組織名の取得/修正。
export const getOrgDetail = async (sosikiCode) => {
  const response = await fetch(`${API_BASE_URL}/org_tree/sosiki/${encodeURIComponent(sosikiCode)}/detail`,
    { headers: getAuthHeaders() });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return await response.json();
};
export const setOrgFields = async (sosikiCode, fields) => {
  const response = await fetch(`${API_BASE_URL}/org_tree/sosiki/${encodeURIComponent(sosikiCode)}/set_fields`, {
    method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(parseErrorDetail(errorData, response.status));
  }
  return await response.json();
};

// 3組織補正: 組織コードのクリックで開く研究者ポップアップ
// (在籍/離任・役職・着任離任年・他組織を含む在籍履歴)。
export const getOrgResearchers = async (sosikiCode) => {
  const response = await fetch(
    `${API_BASE_URL}/org_tree/sosiki/${encodeURIComponent(sosikiCode)}/researchers`,
    { headers: getAuthHeaders() });
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(parseErrorDetail(errorData, response.status));
  }
  return await response.json();
};

// V2 3組織補正: 組織名の変更 (ダブルクリックで値修正)。
//
// 改名すると同じ親の下の既存組織と同じものになる場合、サーバーは 409 と
// reason='duplicate_org' を返して何も書かない。呼び出し側がその場合だけ
// force=true で再送できるよう、重複の情報を例外に載せて投げる。
export const renameOrg = async (sosikiCode, sosikiName, { force = false } = {}) => {
  const response = await fetch(`${API_BASE_URL}/org_tree/sosiki/${encodeURIComponent(sosikiCode)}/rename`, {
    method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ sosiki_name: sosikiName, force_duplicate: force }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    const err = new Error(parseErrorDetail(errorData, response.status));
    const d = errorData && errorData.detail;
    if (response.status === 409 && d && d.reason === 'duplicate_org') err.duplicateOrg = d;
    throw err;
  }
  return await response.json();
};

// 組織単位クロール&解析を起動 (researcher_urls + 組織トップURL)。
export const crawlSosiki = async (sosikiCode) => {
  const response = await fetch(`${API_BASE_URL}/org_tree/sosiki/${encodeURIComponent(sosikiCode)}/crawl`, {
    method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return await response.json();
};

// 組織名 + site:機関ドメイン で Google(Serper) 検索し、トップ/研究者一覧URLの候補を得る。
export const searchOrgUrl = async (sosikiCode, kind = 'top') => {
  const response = await fetch(`${API_BASE_URL}/org_tree/sosiki/${encodeURIComponent(sosikiCode)}/search_url`, {
    method: 'POST',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ kind }),
  });
  if (!response.ok) {
    const t = await response.text().catch(() => '');
    throw new Error(`HTTP ${response.status}: ${t}`);
  }
  return await response.json();
};

// /compare ツリーから組織のトップURL更新/削除 / 研究者一覧URL追加/削除 (audit_log記録)。
// clear_top / clear_member を渡すと、その URL を削除 (誤登録URLのクリア用)。
// #163: 「まとめページ？」の印を、作業者が確かめて外す (この組織の研究者一覧だった)。
// undo=true で取り消し (印がまた出る)。
export const markMemberUrlNotSummary = async (sosikiCode, url, undo = false) => {
  const response = await fetch(`${API_BASE_URL}/org_tree/member_url/not_summary${undo ? '/undo' : ''}`, {
    method: 'POST',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ sosiki_code: sosikiCode, url }),
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return await response.json();
};

export const setOrgUrl = async (
  sosikiCode,
  { kikan_code, top_url, member_url, clear_top, clear_member, allow_shared } = {},
) => {
  const response = await fetch(`${API_BASE_URL}/org_tree/sosiki/${encodeURIComponent(sosikiCode)}/set_url`, {
    method: 'POST',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ kikan_code, top_url, member_url, clear_top, clear_member, allow_shared }),
  });
  // 409 = まとめページ。どの組織と共有しているかを呼び出し側へ渡す
  // (作業者が「同じラボの別名組織だから通す」と判断できるようにするため)。
  if (response.status === 409) {
    const body = await response.json().catch(() => null);
    const d = (body && body.detail) || {};
    const err = new Error(d.message || 'この URL は他の組織の研究者一覧にもなっています');
    err.sharedMemberUrl = true;
    err.sharedWith = d.shared_with || [];
    err.url = d.url || member_url;
    throw err;
  }
  if (!response.ok) {
    const txt = await response.text().catch(() => '');
    throw new Error(`HTTP ${response.status}: ${txt}`);
  }
  return await response.json();
};

// /compare ツリーで研究者の役職 (mt_kennkyuusya_sosiki.yakusyoku) を直接編集
// (audit_log 記録・undo 可)。yakusyoku を空文字/未指定にすると役職を空にする。
export const setResearcherRole = async (kennkyuusyaCode, { sosiki_code, yakusyoku } = {}) => {
  const response = await fetch(`${API_BASE_URL}/org_tree/researcher/${encodeURIComponent(kennkyuusyaCode)}/set_role`, {
    method: 'POST',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ sosiki_code, yakusyoku }),
  });
  if (!response.ok) {
    const txt = await response.text().catch(() => '');
    throw new Error(`HTTP ${response.status}: ${txt}`);
  }
  return await response.json();
};

// #87: クロールに出てこない研究者を「4 研究者補正」に手で足す。マスタへ直接
// 入れるのではなく承認待ち (tmp_crawl_result) に新規行として積むので、
// 「5 確定」の承認・同姓同名/異動の判定・監査ログ・取り消しがそのまま効く。
// #92: kennkyuusya_code を渡すと、承認時に新しいコードを採番せず、その既存研究者の
// 所属として追加される (同じ人に 2 つ目のコードを振らないため)。
export const addResearcher = async ({ kikan_code, sosiki_code, researcher_name,
                                      researcher_kana = null, yakusyoku = null,
                                      kennkyuusya_code = null } = {}) => {
  const response = await fetch(`${API_BASE_URL}/org_tree/researcher/add`, {
    method: 'POST',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ kikan_code, sosiki_code, researcher_name, researcher_kana,
                           yakusyoku, kennkyuusya_code }),
  });
  if (!response.ok) {
    const txt = await response.text().catch(() => '');
    let detail = txt;
    try { detail = JSON.parse(txt).detail || txt; } catch (e) { /* テキストのまま */ }
    throw new Error(detail || `HTTP ${response.status}`);
  }
  return await response.json();
};

// ===== #97: マスタ参照画面 (機関・組織・研究者の検索/参照/編集/CSV入出力) =====

const masterRequest = async (path, { method = 'GET', body = null } = {}) => {
  const response = await fetch(`${API_BASE_URL}/master${path}`, {
    method,
    headers: body
      ? { ...getAuthHeaders(), 'Content-Type': 'application/json' }
      : getAuthHeaders(),
    body: body ? JSON.stringify(body) : undefined,
  });
  if (response.status === 401) { handleUnauthorized(); throw new Error('セッションが切れています。再ログインしてください。'); }
  if (!response.ok) {
    const txt = await response.text().catch(() => '');
    let detail = txt;
    try { detail = JSON.parse(txt).detail || txt; } catch (e) { /* テキストのまま */ }
    throw new Error(detail || `HTTP ${response.status}`);
  }
  return await response.json();
};

export const listMasterKikan = (q, limit = 50) =>
  masterRequest(`/kikan?q=${encodeURIComponent(q || '')}&limit=${limit}`);

// 既定は正規データのみ (仮登録・削除済みを除く)。includeDeleted で削除済みも出す。
export const getMasterKikanTree = (kikanCode, includeDeleted = false) =>
  masterRequest(`/kikan/${encodeURIComponent(kikanCode)}/tree?include_deleted=${includeDeleted ? 'true' : 'false'}`);

export const getMasterSosikiMembers = (sosikiCode, includeDeleted = false) =>
  masterRequest(`/sosiki/${encodeURIComponent(sosikiCode)}/members?include_deleted=${includeDeleted ? 'true' : 'false'}`);

export const searchMaster = (q, kind = 'all', limit = 30) =>
  masterRequest(`/search?q=${encodeURIComponent(q)}&kind=${kind}&limit=${limit}`);

export const editMasterSosiki = (sosikiCode, fields) =>
  masterRequest(`/sosiki/${encodeURIComponent(sosikiCode)}/edit`,
                { method: 'POST', body: fields });

export const editMasterResearcher = (kennkyuusyaCode, fields) =>
  masterRequest(`/researcher/${encodeURIComponent(kennkyuusyaCode)}/edit`,
                { method: 'POST', body: fields });

// 機関の編集・新規登録。**本システムが mt_kikan に書く唯一の入口** (2026-10-05)。
// これが無かったため、担当者は LDB 本体で直接編集するしかなかった。
export const editMasterKikan = (kikanCode, fields) =>
  masterRequest(`/kikan/${encodeURIComponent(kikanCode)}/edit`,
                { method: 'POST', body: fields });

export const addMasterKikan = (fields) =>
  masterRequest('/kikan/add', { method: 'POST', body: fields });

export const previewMasterImport = (body) =>
  masterRequest('/import/preview', { method: 'POST', body });

export const applyMasterImport = (body) =>
  masterRequest('/import/apply', { method: 'POST', body });

// CSV は認証ヘッダが要るので、リンクではなく fetch してから保存させる。
export const downloadMasterCsv = async ({ kikan_code, sosiki_code = null,
                                          kind = 'organizations' } = {}) => {
  const params = new URLSearchParams({ kikan_code, kind });
  if (sosiki_code) params.set('sosiki_code', sosiki_code);
  const response = await fetch(`${API_BASE_URL}/master/export?${params.toString()}`,
                               { headers: getAuthHeaders() });
  if (response.status === 401) { handleUnauthorized(); throw new Error('セッションが切れています。再ログインしてください。'); }
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const disp = response.headers.get('Content-Disposition') || '';
  const m = disp.match(/filename="?([^";]+)"?/);
  const filename = m ? m[1] : `${kind}_${kikan_code}.csv`;
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return filename;
};

// #95: 同姓同名の候補について、経歴・研究内容から「同一人物か」を判定する。
// 参考情報を返すだけで、マスタには何も書かない (コードを付けるのは異動承認)。
export const judgeSamePerson = async (tmpId) => {
  const response = await fetch(
    `${API_BASE_URL}/updates/pending_updates/${tmpId}/judge_same_person`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    });
  if (!response.ok) {
    const txt = await response.text().catch(() => '');
    let detail = txt;
    try { detail = JSON.parse(txt).detail || txt; } catch (e) { /* テキストのまま */ }
    throw new Error(detail || `HTTP ${response.status}`);
  }
  return await response.json();
};

// #100: 研究者の氏名・カナを直す (LDB に登録済みの人。マスタ直接修正・undo 可)。
export const setResearcherName = async (kennkyuusyaCode, { name, kana = undefined } = {}) => {
  const body = { name };
  if (kana !== undefined) body.kana = kana;
  const response = await fetch(
    `${API_BASE_URL}/org_tree/researcher/${encodeURIComponent(kennkyuusyaCode)}/set_name`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  if (!response.ok) {
    const txt = await response.text().catch(() => '');
    let detail = txt;
    try { detail = JSON.parse(txt).detail || txt; } catch (e) { /* テキストのまま */ }
    throw new Error(detail || `HTTP ${response.status}`);
  }
  return await response.json();
};

// #92: 研究者の「離任」を記録する (mt_kennkyuusya_sosiki.rininbi に年を入れる)。
// LDB の在籍判定は del_flg=0 かつ rininbi 空なので、離任は削除ではなくこの列。
// clear=true で離任を取り消す (在籍中に戻す)。
export const setResearcherDeparture = async (kennkyuusyaCode, { sosiki_code,
                                                                rininbi = null,
                                                                clear = false } = {}) => {
  const response = await fetch(
    `${API_BASE_URL}/org_tree/researcher/${encodeURIComponent(kennkyuusyaCode)}/depart`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ sosiki_code, rininbi, clear }),
    });
  if (!response.ok) {
    const txt = await response.text().catch(() => '');
    let detail = txt;
    try { detail = JSON.parse(txt).detail || txt; } catch (e) { /* テキストのまま */ }
    throw new Error(detail || `HTTP ${response.status}`);
  }
  return await response.json();
};

// /compare ツリーに組織を追加 (mt_sosiki INSERT, sosiki_code 自動採番)。
// adopt_children=true: 親の既存の子を新組織の配下へ付け替え (=階層の間に挿入)。
// #140 第2段: owner_* / source_* は企業の内部組織 (mt_sosiki_maker) に入れるとき
// だけ使う。採番は一律 MK+6桁 なので、持ち主は owner_hojin_cd/name で表す。
// source_* は「どのアンマッチから作ったか」の出所 (あとから見直すときに要る)。
// #140 ③ そもそも組織でない: 除外ワードを足したら紐付けがどう動くかを
// **足す前に**数える。2026-09-24 に 25 語をまとめて足す試算をしたら差し引き
// −405 で悪化した。語ごとに数えれば判断できる。読み取りのみ。
export const getXrefExclusionImpact = async (word) => {
  const response = await fetch(
    `${API_BASE_URL}/xref/exclusion_impact?word=${encodeURIComponent(word)}`,
    { headers: getAuthHeaders() });
  if (response.status === 401) { handleUnauthorized(); throw new Error('セッションが切れています。再ログインしてください。'); }
  if (!response.ok) {
    const e = await response.json().catch(() => ({}));
    throw new Error(typeof e.detail === 'string' ? e.detail : `HTTP ${response.status}`);
  }
  return response.json();
};

export const addOrg = async ({ kikan_code, parent_sosiki_code, sosiki_name, labo_flg, adopt_children = false, url = null,
                               apply_now = false, require_public = false,
                               owner_hojin_cd = null, owner_hojin_name = null,
                               source_p_user = null, source_dealer = null } = {}) => {
  const response = await fetch(`${API_BASE_URL}/org_tree/sosiki/add`, {
    method: 'POST',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ kikan_code, parent_sosiki_code, sosiki_name, labo_flg, adopt_children, url,
                           apply_now, require_public,
                           owner_hojin_cd, owner_hojin_name,
                           source_p_user, source_dealer }),
  });
  if (!response.ok) {
    const t = await response.text().catch(() => '');
    // #140: 企業・重複などは detail に理由が入る。そのまま画面へ出す。
    let msg = `HTTP ${response.status}`;
    try { msg = JSON.parse(t).detail || msg; } catch (e) { msg = t || msg; }
    throw new Error(msg);
  }
  return await response.json();
};

// 組織を SOFT_DELETE (del_flg=1, undo可能)。
// mode: 'gone'(消滅=del_flg1) / 'duplicate'(ダブり=del_flg2) / 'merge'(統合=del_flg1+統合先をaudit_logへ)
export const deleteOrg = async (sosikiCode, { kikan_code, mode = 'gone', merged_into_sosiki_code = null } = {}) => {
  const response = await fetch(`${API_BASE_URL}/org_tree/sosiki/${encodeURIComponent(sosikiCode)}/delete`, {
    method: 'POST',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ kikan_code, mode, merged_into_sosiki_code }),
  });
  if (!response.ok) { const t = await response.text().catch(() => ''); throw new Error(`HTTP ${response.status}: ${t}`); }
  return await response.json();
};

// #73: 論理削除した組織を元に戻す (del_flg=0)。削除済みの行はツリーに残るので、
// その行の右クリックから直接呼べる。
export const restoreOrg = async (sosikiCode, { kikan_code } = {}) => {
  const response = await fetch(`${API_BASE_URL}/org_tree/sosiki/${encodeURIComponent(sosikiCode)}/restore`, {
    method: 'POST',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ kikan_code }),
  });
  if (!response.ok) { const t = await response.text().catch(() => ''); throw new Error(`HTTP ${response.status}: ${t}`); }
  return await response.json();
};

// #77: 「他の組織に統合」の取り消し。del_flg を戻すだけの restoreOrg と違い、
// 統合時に親へ格上げした子と、旧→新コードの対応 (deletetable_all) も戻す。
export const unmergeOrg = async (sosikiCode, { kikan_code } = {}) => {
  const response = await fetch(`${API_BASE_URL}/org_tree/sosiki/${encodeURIComponent(sosikiCode)}/unmerge`, {
    method: 'POST',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ kikan_code }),
  });
  if (!response.ok) { const t = await response.text().catch(() => ''); throw new Error(`HTTP ${response.status}: ${t}`); }
  return await response.json();
};

export const getOrgTreeComparison = async (kikanCode) => {
  const response = await fetch(`${API_BASE_URL}/org_tree/compare/${encodeURIComponent(kikanCode)}`, { headers: getAuthHeaders() });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return await response.json();
};

// 推定ツリーノードクリック → そのノードを推定した元URLとLLM出力ログ
export const getOrgTreeNodeDetails = async (runId, name) => {
  const qs = new URLSearchParams({ name });
  const response = await fetch(`${API_BASE_URL}/org_tree/runs/${runId}/node?${qs}`, { headers: getAuthHeaders() });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return await response.json();
};

// Heatmap for one institution's latest crawl (org/researcher state cells).
export const getCrawlResultsHeatmap = async (kikanCode) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/updates/results/heatmap?kikan_code=${encodeURIComponent(kikanCode)}`,
      { headers: getAuthHeaders() }
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching crawl results heatmap:", error);
    return null;
  }
};

export const getCrawlDiffs = async (page = 1, per_page = 20, filters = {}) => {
  try {
    let url = `${API_BASE_URL}/crawl_diffs/?page=${page}&per_page=${per_page}`;
    if (filters.kikan_code) url += `&kikan_code=${encodeURIComponent(filters.kikan_code)}`;
    if (filters.change_type) url += `&change_type=${encodeURIComponent(filters.change_type)}`;
    if (filters.target_type) url += `&target_type=${encodeURIComponent(filters.target_type)}`;

    const response = await fetch(url, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching crawl diffs:", error);
    return null;
  }
};

export const getCrawlDiffSummary = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/crawl_diffs/summary`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching crawl diff summary:", error);
    return null;
  }
};

export const getCrawlDiffDetail = async (id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/crawl_diffs/${id}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(parseErrorDetail(errorData, response.status));
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching crawl diff detail:", error);
    throw error;
  }
};

// Auto-process API: action ALL PENDING rows whose recommendation is approve/reject.
// dry_run=true returns counts only; set dry_run=false to commit.
export const autoProcessPendingUpdates = async ({ dry_run = true, process_id = null, kikan_code = null, max_rows = 2000, include = ['approve', 'reject'] } = {}) => {
  const response = await fetch(`${API_BASE_URL}/updates/auto_process`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ dry_run, process_id, kikan_code, max_rows, include }),
  });
  if (response.status === 401) { handleUnauthorized(); throw new Error('セッションが切れています'); }
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(parseErrorDetail(errorData, response.status));
  }
  return await response.json();
};

// 監査ログ (Audit Log) API
export const getAuditLog = async (filters = {}, page = 1, limit = 50) => {
  const params = new URLSearchParams({ page, limit });
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') {
      params.append(k, v);
    }
  });
  const response = await fetch(`${API_BASE_URL}/updates/audit_log?${params.toString()}`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `HTTP ${response.status}`);
  }
  return response.json();
};

export const getAuditLogByRow = async ({ master_table, kikan_code, sosiki_code, kennkyuusya_code }) => {
  const params = new URLSearchParams({ master_table, kikan_code });
  if (sosiki_code) params.append('sosiki_code', sosiki_code);
  if (kennkyuusya_code) params.append('kennkyuusya_code', kennkyuusya_code);
  const response = await fetch(`${API_BASE_URL}/updates/audit_log/by_row?${params.toString()}`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `HTTP ${response.status}`);
  }
  return response.json();
};

export const getAuditLogSummary = async ({ since, until } = {}) => {
  const params = new URLSearchParams();
  if (since) params.append('since', since);
  if (until) params.append('until', until);
  const qs = params.toString();
  const response = await fetch(`${API_BASE_URL}/updates/audit_log/summary${qs ? `?${qs}` : ''}`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `HTTP ${response.status}`);
  }
  return response.json();
};

// 正規マスタ(mt_*)への反映状況サマリ + 未反映一覧。mt_* は顧客側の日次バッチで
// 洗い替えされるため、承認しても正規に残らないことがある。
export const getMasterSync = async () => {
  const response = await fetch(`${API_BASE_URL}/updates/master_sync`, { headers: getAuthHeaders() });
  if (response.status === 401) { handleUnauthorized(); throw new Error('セッションが切れています。再ログインしてください。'); }
  if (!response.ok) {
    const e = await response.json().catch(() => ({}));
    throw new Error(e.detail || `HTTP ${response.status}`);
  }
  return await response.json();
};

// マスタ変更のまとめ取消。既定は dry_run=true (何も変更せず、実行結果だけ返す)。
export const runMasterRollback = async (params = {}) => {
  const response = await fetch(`${API_BASE_URL}/updates/master_rollback`, {
    method: 'POST', headers: getAuthHeaders(),
    body: JSON.stringify({ dry_run: true, ...params }),
  });
  if (response.status === 401) { handleUnauthorized(); throw new Error('セッションが切れています。再ログインしてください。'); }
  if (!response.ok) {
    const e = await response.json().catch(() => ({}));
    throw new Error(typeof e.detail === 'string' ? e.detail : `HTTP ${response.status}`);
  }
  return await response.json();
};

// 反映状況をいま検証する (audit_log の最終状態 vs 現在のマスタ)。
export const verifyMasterSync = async () => {
  const response = await fetch(`${API_BASE_URL}/updates/master_sync/verify`, {
    method: 'POST', headers: getAuthHeaders(),
  });
  if (response.status === 401) { handleUnauthorized(); throw new Error('セッションが切れています。再ログインしてください。'); }
  if (!response.ok) {
    const e = await response.json().catch(() => ({}));
    throw new Error(e.detail || `HTTP ${response.status}`);
  }
  return await response.json();
};

// 仮確定プール (audit_log の最終状態) を、いまの接続先DBへ流す (replay)。
// 既定は dry_run=true (実際に書いた結果を返してから破棄する)。日次バッチの
// 洗い替えで消えた分を戻す処理であり、正規DBへ切り替えたあとの初回反映も同じ。
export const runMasterApply = async (params = {}) => {
  const response = await fetch(`${API_BASE_URL}/updates/master_apply`, {
    method: 'POST', headers: getAuthHeaders(),
    body: JSON.stringify({ dry_run: true, ...params }),
  });
  if (response.status === 401) { handleUnauthorized(); throw new Error('セッションが切れています。再ログインしてください。'); }
  if (!response.ok) {
    const e = await response.json().catch(() => ({}));
    throw new Error(typeof e.detail === 'string' ? e.detail : `HTTP ${response.status}`);
  }
  return await response.json();
};

// audit_log → 正規マスタ(mt_*) まとめ反映用の差分レポート + SQL を生成 (自動適用しない)。
// 既定は未反映 (LOST/CONFLICT) のみ。onlyUnapplied=false で全件。
export const getMasterExport = async (since = null, onlyUnapplied = true) => {
  const params = new URLSearchParams();
  if (since) params.set('since', since);
  params.set('only_unapplied', onlyUnapplied ? 'true' : 'false');
  const qs = `?${params.toString()}`;
  const response = await fetch(`${API_BASE_URL}/updates/master_export${qs}`, { headers: getAuthHeaders() });
  if (response.status === 401) { handleUnauthorized(); throw new Error('セッションが切れています。再ログインしてください。'); }
  if (!response.ok) {
    const e = await response.json().catch(() => ({}));
    throw new Error(e.detail || `HTTP ${response.status}`);
  }
  return await response.json();
};

// 承認した記録を仮確定プールから下げる。**マスタには何も書かない。**
// undo (マスタに入った変更を元に戻す) とは別で、こちらは「マスタへは
// 入れないことにする」宣言。競合は下げない限り毎朝 CONFLICT として残る。
export const withdrawAuditEntries = async (auditIds, reason = null) => {
  const response = await fetch(`${API_BASE_URL}/updates/audit_log/withdraw`, {
    method: 'POST', headers: getAuthHeaders(),
    body: JSON.stringify({ audit_ids: auditIds, reason }),
  });
  if (response.status === 401) { handleUnauthorized(); throw new Error('セッションが切れています。再ログインしてください。'); }
  if (!response.ok) {
    const e = await response.json().catch(() => ({}));
    throw new Error(typeof e.detail === 'string' ? e.detail : `HTTP ${response.status}`);
  }
  return response.json();
};

export const undoAuditEntry = async (auditId, reason = null) => {
  const response = await fetch(`${API_BASE_URL}/updates/audit_log/${auditId}/undo`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ reason }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    // FastAPI puts structured errors in detail
    const detail = errorData.detail;
    const msg = typeof detail === 'string' ? detail : (detail?.reason || JSON.stringify(detail) || `HTTP ${response.status}`);
    throw new Error(msg);
  }
  return response.json();
};

// #133: 研究者の「新規登録」を取り消す。画面は audit_log の id を持っていないので、
// 組織と研究者のコードをサーバへ渡して、その所属を作った承認を引いてもらう。
// クロールが拾った氏名と LDB の既存登録が表記違いで二重登録になったときに使う。
export const undoResearcherRegistration = async (sosikiCode, kennkyuusyaCode, reason = null) => {
  const response = await fetch(`${API_BASE_URL}/updates/researcher/undo_registration`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      sosiki_code: sosikiCode, kennkyuusya_code: kennkyuusyaCode, reason,
    }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const detail = errorData.detail;
    const msg = typeof detail === 'string'
      ? detail : (detail?.reason || JSON.stringify(detail) || `HTTP ${response.status}`);
    throw new Error(msg);
  }
  return response.json();
};

// === 調査の着手管理 (investigation claims / ソフトロック) ==================
// --- 調査状態 (仮確定モデル · プロセスV2 P1) ---------------------------------
// 組織ノード単位の 組織仮確定/研究者仮確定/確定/リ確認依頼 の打刻状態。
export const getInvestigationStatus = async (kikanCode) => {
  const response = await fetch(`${API_BASE_URL}/investigation_status/${encodeURIComponent(kikanCode)}`,
    { headers: getAuthHeaders() });
  if (response.status === 401) { handleUnauthorized(); return { status: {} }; }
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return await response.json();
};

// #53: 研究者単位の仮確定 (組織単位の researcher_fixed とは別。キーは氏名)。
export const getResearcherInvStatus = async (kikanCode) => {
  const response = await fetch(
    `${API_BASE_URL}/investigation_status/${encodeURIComponent(kikanCode)}/researchers`,
    { headers: getAuthHeaders() });
  if (response.status === 401) { handleUnauthorized(); return { status: {} }; }
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return await response.json();
};

export const setResearcherInvStatus = async ({ kikan_code, sosiki_code, name_keys, value,
                                               display_name = null, kennkyuusya_code = null }) => {
  const response = await fetch(`${API_BASE_URL}/investigation_status/researchers/set`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ kikan_code, sosiki_code, name_keys, value, display_name, kennkyuusya_code }),
  });
  if (response.status === 401) { handleUnauthorized(); throw new Error('セッションが切れています。再ログインしてください。'); }
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(parseErrorDetail(errorData, response.status));
  }
  return await response.json();
};

// field: 'org_fixed' | 'researcher_fixed' | 'finalized' | 'rmap_check'
export const setInvestigationStatus = async ({ kikan_code, sosiki_codes, field, value, include_descendants = false, note = null }) => {
  const response = await fetch(`${API_BASE_URL}/investigation_status/set`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ kikan_code, sosiki_codes, field, value, include_descendants, note }),
  });
  if (response.status === 401) { handleUnauthorized(); throw new Error('セッションが切れています。再ログインしてください。'); }
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(parseErrorDetail(errorData, response.status));
  }
  return await response.json();
};

export const getClaims = async (kikanCode = null) => {
  const qs = kikanCode ? `?kikan_code=${encodeURIComponent(kikanCode)}` : '';
  const response = await fetch(`${API_BASE_URL}/claims/${qs}`, { headers: getAuthHeaders() });
  if (response.status === 401) { handleUnauthorized(); return { claims: [] }; }
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return await response.json();
};

export const claimInvestigation = async ({ kikan_code, sosiki_code = null, force = false, note = null }) => {
  const response = await fetch(`${API_BASE_URL}/claims/claim`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ kikan_code, sosiki_code, force, note }),
  });
  if (response.status === 401) { handleUnauthorized(); throw new Error('セッションが切れています。再ログインしてください。'); }
  if (response.status === 409) {
    // Soft lock: someone else holds this unit. Surface the holder so the UI
    // can offer a takeover confirmation.
    const data = await response.json().catch(() => ({}));
    const err = new Error('already_claimed');
    err.conflict = (data && data.detail) || data || {};
    throw err;
  }
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(parseErrorDetail(errorData, response.status));
  }
  return await response.json();
};

export const releaseInvestigation = async ({ kikan_code, sosiki_code = null }) => {
  const response = await fetch(`${API_BASE_URL}/claims/release`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ kikan_code, sosiki_code }),
  });
  if (response.status === 401) { handleUnauthorized(); throw new Error('セッションが切れています。再ログインしてください。'); }
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(parseErrorDetail(errorData, response.status));
  }
  return await response.json();
};

// 複数の第一組織をまとめて着手。他人着手中があり force=false なら 409 (err.conflict
// に一覧) を投げ、UI が一括引き継ぎ確認できるようにする。
export const claimInvestigationBulk = async ({ kikan_code, sosiki_codes, force = false, note = null }) => {
  const response = await fetch(`${API_BASE_URL}/claims/claim_bulk`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ kikan_code, sosiki_codes, force, note }),
  });
  if (response.status === 401) { handleUnauthorized(); throw new Error('セッションが切れています。再ログインしてください。'); }
  if (response.status === 409) {
    const data = await response.json().catch(() => ({}));
    const err = new Error('already_claimed');
    err.conflict = (data && data.detail) || data || {};
    throw err;
  }
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(parseErrorDetail(errorData, response.status));
  }
  return await response.json();
};

// 複数の第一組織の着手をまとめて解除 (本人/admin のみ)。
export const releaseInvestigationBulk = async ({ kikan_code, sosiki_codes }) => {
  const response = await fetch(`${API_BASE_URL}/claims/release_bulk`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ kikan_code, sosiki_codes }),
  });
  if (response.status === 401) { handleUnauthorized(); throw new Error('セッションが切れています。再ログインしてください。'); }
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(parseErrorDetail(errorData, response.status));
  }
  return await response.json();
};

// === ご意見・ご要望 (Feedback) ============================================
export const submitFeedback = async ({ message, category = 'other', page_path = null, image_data = null, images = null }) => {
  const response = await fetch(`${API_BASE_URL}/feedback/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    // images[] (複数) が優先。単一 image_data は後方互換で残す。
    body: JSON.stringify({ message, category, page_path, image_data, images }),
  });
  if (response.status === 401) { handleUnauthorized(); throw new Error('セッションが切れています。再ログインしてください。'); }
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(parseErrorDetail(errorData, response.status));
  }
  return await response.json();
};

export const getFeedbackList = async ({ status = '', category = '', user_email = '', page = 1, limit = 50 } = {}) => {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (category) params.set('category', category);
  if (user_email) params.set('user_email', user_email);
  params.set('page', String(page));
  params.set('limit', String(limit));
  const response = await fetch(`${API_BASE_URL}/feedback/?${params.toString()}`, { headers: getAuthHeaders() });
  if (response.status === 401) { handleUnauthorized(); throw new Error('セッションが切れています。再ログインしてください。'); }
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return await response.json();
};

export const getFeedbackSummary = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/feedback/summary`, { headers: getAuthHeaders() });
    if (response.status === 401) { handleUnauthorized(); return null; }
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return await response.json();
  } catch (e) {
    console.error('Error fetching feedback summary:', e);
    return null;
  }
};

export const getFeedbackImage = async (id) => {
  const response = await fetch(`${API_BASE_URL}/feedback/${id}/image`, { headers: getAuthHeaders() });
  if (response.status === 401) { handleUnauthorized(); throw new Error('セッションが切れています。再ログインしてください。'); }
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return await response.json();
};

export const updateFeedbackStatus = async (id, status, adminNote = null) => {
  const response = await fetch(`${API_BASE_URL}/feedback/${id}/status`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify({ status, admin_note: adminNote }),
  });
  if (response.status === 401) { handleUnauthorized(); throw new Error('セッションが切れています。再ログインしてください。'); }
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(parseErrorDetail(errorData, response.status));
  }
  return await response.json();
};

// 投稿の編集 (本人のみ・本文/種別)
export const editFeedback = async (id, { message, category = 'other', images_append = null }) => {
  const response = await fetch(`${API_BASE_URL}/feedback/${id}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify({ message, category, images_append }),
  });
  if (response.status === 401) { handleUnauthorized(); throw new Error('セッションが切れています。再ログインしてください。'); }
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(parseErrorDetail(errorData, response.status));
  }
  return await response.json();
};

// 投稿の削除 (本人 または 管理者)
export const deleteFeedback = async (id) => {
  const response = await fetch(`${API_BASE_URL}/feedback/${id}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (response.status === 401) { handleUnauthorized(); throw new Error('セッションが切れています。再ログインしてください。'); }
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(parseErrorDetail(errorData, response.status));
  }
  return await response.json();
};

// 管理者からの返信 (1投稿1つ・空文字で消去)
export const setFeedbackComment = async (id, comment) => {
  const response = await fetch(`${API_BASE_URL}/feedback/${id}/comment`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify({ comment }),
  });
  if (response.status === 401) { handleUnauthorized(); throw new Error('セッションが切れています。再ログインしてください。'); }
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(parseErrorDetail(errorData, response.status));
  }
  return await response.json();
};

// ドキュメントAPI
//
// このファイルの他の呼び出しと同じく getAuthHeaders() を通す。docs だけが
// 素の fetch のままだったため、サーバー側に認証を掛けると 401 になり、
// サイドバーの「ガイドライン」メニューが黙って空になる状態だった。
export const getDocsList = async () => {
  // 未ログインのときは呼ばない。ログイン画面の裏で 401 を出しても意味がなく、
  // ログイン後に呼び直される（App.js の useEffect が user を見ている）
  if (!localStorage.getItem('token')) return [];
  try {
    const response = await fetch(`${API_BASE_URL}/docs`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data.files || [];
  } catch (error) {
    // 空配列を返す形は変えない（App.js が一覧としてそのまま使っている）。
    // ただし「取れなかった」と「1 件も無い」は別なので、ログには残す
    console.error("Error fetching docs list:", error);
    return [];
  }
};

// Scheduler enable/disable runtime toggle (Redis-backed).
// GET returns {enabled: bool}; PUT writes the same shape.
export const getSchedulerStatus = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/system/scheduler`, {
      headers: getAuthHeaders(),
    });
    if (response.status === 401) { handleUnauthorized(); return null; }
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return await response.json();
  } catch (e) {
    console.error('Error fetching scheduler status:', e);
    return null;
  }
};

// Guardrails (HTTP request caps, LLM call caps, per-host pause).
export const getGuardrails = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/system/guardrails`, {
      headers: getAuthHeaders(),
    });
    if (response.status === 401) { handleUnauthorized(); return null; }
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return await response.json();
  } catch (e) {
    console.error('Error fetching guardrails:', e);
    return null;
  }
};

export const updateGuardrailLimits = async (limits) => {
  const response = await fetch(`${API_BASE_URL}/system/guardrails/limits`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(limits),
  });
  if (response.status === 401) { handleUnauthorized(); throw new Error('セッションが切れています'); }
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(parseErrorDetail(errorData, response.status));
  }
  return await response.json();
};

// ドメインポリシー (Phase2): 遭遇した外部ドメインの一覧 + 深掘り/浅取り/ブロック判定。
export const getDomainPolicies = async ({ undecided = false, classification = '', limit = 300 } = {}) => {
  const params = new URLSearchParams();
  if (undecided) params.set('undecided', 'true');
  if (classification) params.set('classification', classification);
  params.set('limit', String(limit));
  const response = await fetch(`${API_BASE_URL}/domain_policy/?${params.toString()}`, { headers: getAuthHeaders() });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return await response.json();
};

export const setDomainPolicy = async (domain, policy) => {
  const response = await fetch(`${API_BASE_URL}/domain_policy/${encodeURIComponent(domain)}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify({ policy }),
  });
  if (response.status === 401) { handleUnauthorized(); throw new Error('セッションが切れています'); }
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(parseErrorDetail(errorData, response.status));
  }
  return await response.json();
};

// クロール時間帯制限 ("" = 常時許可 / "SS-EE" = JST の許可窓)。
export const getCrawlWindow = async () => {
  const response = await fetch(`${API_BASE_URL}/system/crawl-window`, { headers: getAuthHeaders() });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return await response.json();
};

export const setCrawlWindow = async (allowedHours) => {
  const response = await fetch(`${API_BASE_URL}/system/crawl-window`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify({ allowed_hours: allowedHours }),
  });
  if (response.status === 401) { handleUnauthorized(); throw new Error('セッションが切れています'); }
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(parseErrorDetail(errorData, response.status));
  }
  return await response.json();
};

export const setHostPause = async (host, paused, hours = null) => {
  const response = await fetch(`${API_BASE_URL}/system/guardrails/host/${encodeURIComponent(host)}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify({ paused, hours }),
  });
  if (response.status === 401) { handleUnauthorized(); throw new Error('セッションが切れています'); }
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(parseErrorDetail(errorData, response.status));
  }
  return await response.json();
};

export const setSchedulerStatus = async (enabled) => {
  const response = await fetch(`${API_BASE_URL}/system/scheduler`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify({ enabled }),
  });
  if (response.status === 401) { handleUnauthorized(); throw new Error('セッションが切れています'); }
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(parseErrorDetail(errorData, response.status));
  }
  return await response.json();
};

export const getDocContent = async (filename) => {
  try {
    const response = await fetch(`${API_BASE_URL}/docs/${filename}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.text();
  } catch (error) {
    // null を返す形は変えない。ManualPage.js が null を受けて
    // 「ドキュメントの読み込みに失敗しました。」を出している
    console.error("Error fetching doc content:", error);
    return null;
  }
};

// ── Budget (予算) cockpit API ────────────────────────────────────────────────
// Thin wrapper that injects auth headers, handles 401, and surfaces a readable
// error message from FastAPI's detail payload (mirrors the rest of this file).
const budgetRequest = async (path, { method = 'GET', body } = {}) => {
  const res = await fetch(`${API_BASE_URL}/budget${path}`, {
    method,
    headers: getAuthHeaders(),
    body: body ? JSON.stringify(body) : undefined,
  });
  if (res.status === 401) { handleUnauthorized(); return null; }
  let data = null;
  try { data = await res.json(); } catch { /* empty body */ }
  if (!res.ok) {
    // #110: 呼び出し側が 409 (登録済みで止めた) などを判別できるよう status を付ける。
    const err = new Error(parseErrorDetail(data, res.status));
    err.status = res.status;
    throw err;
  }
  return data;
};

export const getBudgetSources = () => budgetRequest('/sources');

export const createBudgetSource = (payload) =>
  budgetRequest('/sources', { method: 'POST', body: payload });

export const updateBudgetSource = (id, payload) =>
  budgetRequest(`/sources/${id}/update`, { method: 'POST', body: payload });

// #71: maxAgeMonths = 掲載日が直近Nか月以内の採択情報だけ取り込む (0 = 制限なし)。
export const startBudgetCrawl = (sourceId, maxNewUrls = 200, maxAgeMonths = null) =>
  budgetRequest('/crawl/start', {
    method: 'POST',
    body: { source_id: sourceId, max_new_urls: maxNewUrls, max_age_months: maxAgeMonths },
  });

// 効率化: 採択ページ候補サジェスト / 試し取得プレビュー / 対応状況 / バッチ巡回
export const suggestBudgetTargets = (sourceId) =>
  budgetRequest(`/sources/${sourceId}/suggest_targets`);

export const previewBudgetCrawl = (payload) =>
  budgetRequest('/preview', { method: 'POST', body: payload });

export const getBudgetSourceStatus = () => budgetRequest('/source_status');

export const batchBudgetCrawl = (sourceIds, maxNewUrls = 200, enableFirst = true, maxAgeMonths = null) =>
  budgetRequest('/crawl/batch', {
    method: 'POST',
    body: {
      source_ids: sourceIds, max_new_urls: maxNewUrls,
      enable_first: enableFirst, max_age_months: maxAgeMonths,
    },
  });

// #71: 承認待ち案件へ、対応する公募情報ページの金額・研究年度を当てる。
export const fillBudgetFromKoubo = (payload) =>
  budgetRequest('/results/fill_from_koubo', { method: 'POST', body: payload });

// #84: 承認待ちの「新規／登録済」判定を、いまの yosan と突き合わせ直す。
export const recheckBudgetDedup = (payload) =>
  budgetRequest('/results/recheck_dedup', { method: 'POST', body: payload || {} });

export const getBudgetProcesses = () => budgetRequest('/processes');

export const reanalyzeBudget = (processId) =>
  budgetRequest(`/processes/${processId}/reanalyze`, { method: 'POST' });

export const getBudgetResults = (params = {}) => {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') q.append(k, v);
  });
  const qs = q.toString();
  return budgetRequest(`/results${qs ? `?${qs}` : ''}`);
};

export const resolveBudgetSeido = (q) =>
  budgetRequest(`/resolve/seido?q=${encodeURIComponent(q)}`);
export const resolveBudgetKikan = (q) =>
  budgetRequest(`/resolve/kikan?q=${encodeURIComponent(q)}`);
export const resolveBudgetKennkyuusya = (q) =>
  budgetRequest(`/resolve/kennkyuusya?q=${encodeURIComponent(q)}`);

export const setBudgetCodes = (id, codes) =>
  budgetRequest(`/results/${id}/set_codes`, { method: 'POST', body: codes });

export const setBudgetFields = (id, fields) =>
  budgetRequest(`/results/${id}/set_fields`, { method: 'POST', body: fields });

export const bulkBudgetAmount = (ids, total, mode = 'split', estimate = true) =>
  budgetRequest('/results/bulk_amount', { method: 'POST', body: { ids, total, mode, estimate } });

export const budgetMaintenancePreview = (cutoffYear) =>
  budgetRequest(`/maintenance/preview?cutoff_year=${cutoffYear}`);

export const budgetMaintenancePurge = (cutoffYear) =>
  budgetRequest('/maintenance/purge', { method: 'POST', body: { cutoff_year: cutoffYear } });

export const getBudgetCoverage = (sinceYear) =>
  budgetRequest(`/coverage${sinceYear ? `?since_year=${sinceYear}` : ''}`);

export const getBudgetScheduler = () => budgetRequest('/scheduler');
export const setBudgetScheduler = (enabled) =>
  budgetRequest('/scheduler', { method: 'POST', body: { enabled } });

export const suggestSeidoCode = (prefix = '8002') =>
  budgetRequest(`/seido/suggest_code?prefix=${encodeURIComponent(prefix)}`);
export const createBudgetSeido = (payload) =>
  budgetRequest('/seido', { method: 'POST', body: payload });

export const budgetRematch = (dryRun = true) =>
  budgetRequest(`/rematch?dry_run=${dryRun ? 'true' : 'false'}`, { method: 'POST' });

// #110: allowDuplicate = 同じ案件が yosan にあっても登録する (確認ダイアログの後だけ)。
export const approveBudget = (id, approved = true, overrides = null, allowDuplicate = false) =>
  budgetRequest('/approve', {
    method: 'POST', body: { id, approved, overrides, allow_duplicate: allowDuplicate },
  });

export const approveBudgetBulk = (ids, approved = true) =>
  budgetRequest('/approve_bulk', { method: 'POST', body: { ids, approved } });

export const getBudgetAuditLog = () => budgetRequest('/audit_log');

// #35: 登録済みデータ (yosan) の一覧・修正 (承認画面と分離した修正画面用)。
export const getYosanList = (params = {}) => {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') q.append(k, v);
  });
  return budgetRequest(`/yosan?${q.toString()}`);
};
export const setYosanFields = (seq, fields) =>
  budgetRequest(`/yosan/${seq}/set_fields`, { method: 'POST', body: fields });

export const undoBudget = (auditId, note = null) =>
  budgetRequest(`/audit_log/${auditId}/undo`, { method: 'POST', body: { note } });

// ── アンマッチ紐付け (LDBコード付与) ────────────────────────────────────────
// scm-bio.com 側のアンマッチ一覧を取り込んだキューを見て、AI の提案を承認する画面用。
// budgetRequest と同じ形 (認証ヘッダ・401・detail の読み取り) にそろえる。
const xrefRequest = async (path, { method = 'GET', body } = {}) => {
  const res = await fetch(`${API_BASE_URL}/xref${path}`, {
    method,
    headers: getAuthHeaders(),
    body: body ? JSON.stringify(body) : undefined,
  });
  if (res.status === 401) { handleUnauthorized(); return null; }
  let data = null;
  try { data = await res.json(); } catch { /* empty body */ }
  if (!res.ok) {
    const err = new Error(parseErrorDetail(data, res.status));
    err.status = res.status;
    throw err;
  }
  return data;
};

export const getXrefQueue = (params = {}) => {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') q.append(k, v);
  });
  return xrefRequest(`/queue?${q.toString()}`);
};

export const getXrefItem = (id) => xrefRequest(`/queue/${id}`);

// 候補の作り直し。AI は呼ばないので費用はかからない。
export const getXrefCandidates = (id) => xrefRequest(`/queue/${id}/candidates`);

// 機関の住所・URL (#160)。codes は機関コードの配列。
export const getXrefKikanInfo = (codes) =>
  xrefRequest(`/kikan_info?codes=${encodeURIComponent(codes.join(','))}`);

// #162: 名称で Google 検索 (Serper) して URL の候補を返す。行・語ごとにサーバ側で使い回す。
export const webSearchXrefItem = (id, word = null) =>
  xrefRequest(`/queue/${id}/web_search`, { method: 'POST', body: { word } });

// この 1 件だけ AI に提案させる (1 件あたり約 0.3 円)。
export const suggestXrefItem = (id) => xrefRequest(`/queue/${id}/suggest`, { method: 'POST' });

export const decideXref = (payload) => xrefRequest('/decide', { method: 'POST', body: payload });

export const approveXrefBulk = (queueIds, note = null) =>
  xrefRequest('/approve_bulk', { method: 'POST', body: { queue_ids: queueIds, note } });

export const holdXref = (queueId, note = null) =>
  xrefRequest('/hold', { method: 'POST', body: { queue_id: queueId, note } });

// #126: 仮決定 → 確定。queueIds を省略すると仮決定のすべてを確定する。
export const confirmXref = (queueIds = null) =>
  xrefRequest('/confirm', { method: 'POST', body: { queue_ids: queueIds } });

// #128: 仮決定を取り消して未処理に戻す (選び直しの受け皿)。
export const undecideXref = (queueId) =>
  xrefRequest('/undecide', { method: 'POST', body: { queue_id: queueId } });

export const inquireXref = (payload) => xrefRequest('/inquiry', { method: 'POST', body: payload });

export const getXrefStats = () => xrefRequest('/stats');

// 出どころごとに「その朝 何行取り込んだか」。処理が走っても 0 行しか書かない
// ことがあり (2026-10-21 の TH / PRO_SAP)、それを見つけるための記録。
export const getXrefIngestLog = (days = 14) => xrefRequest(`/ingest_log?days=${days}`);

// メーカー別の LDBコード付与実績 (本体の台帳の累計 + この画面での処理状況)。
export const getXrefMakerStats = (years = 6) => xrefRequest(`/maker_stats?years=${years}`);

// scm-bio.net 側 (NG / コスモバイオ) をいま数え直す。先方の本番 DB を全走査するので
// 30 秒ほどかかる。通常は毎朝 05:20 の控えで足りる。
export const refreshXrefMakerStatsNet = () =>
  xrefRequest('/maker_stats/refresh_net', { method: 'POST' });

// 処理3-1 の「取り込む法人」。設定の実体は scm-bio.com 側の ldb_linkage_target_hojin。
export const getXrefTargetHojin = () => xrefRequest('/target_hojin');

export const saveXrefTargetHojin = (changes) =>
  xrefRequest('/target_hojin', { method: 'POST', body: { changes } });

export const createXrefTargetHojinTable = () =>
  xrefRequest('/target_hojin/create_table', { method: 'POST' });

// この法人の過去分を取り込むよう予約する。backfillFrom は「2021」か「2021-01-01」、
// null で取り消し。**ここでは取り込まない** — 実際に流すのは夜間の処理3-1。
export const reserveXrefHojinBackfill = (hojinCd, backfillFrom) =>
  xrefRequest(`/target_hojin/${encodeURIComponent(hojinCd)}/backfill`,
              { method: 'POST', body: { backfill_from: backfillFrom } });

// 直近の受注 (scm-bio.net の mt_order) だけを数え直す。履歴 1,015 万行の集計は
// 96 秒かかるので画面からは走らせない (毎朝 05:25 の控えに任せる)。
export const syncXrefHojinCandidates = () =>
  xrefRequest('/target_hojin/sync_candidates', { method: 'POST' });

// この法人を LabRadar のテナントとして払い出す（{ enabled: false } で止める）。
// 鍵 (slug) は LabRadar 側の data/tenants/<slug>/ と Onevio の Tenant.slug に
// なるので、後から変えられない。**ここでは受注データを取り込まない** —
// LabRadar が tools/sync_tenants.py でこの 1 行を引いて tenant.yaml にする。
export const saveLabradarTenant = (hojinCd, payload) =>
  xrefRequest(`/target_hojin/${encodeURIComponent(hojinCd)}/labradar`,
              { method: 'POST', body: payload });

// 手で紐付け先を探すときの検索 (マスタ参照画面と同じ API を使う)。
export const searchMasterForXref = async (q, kind = 'all', limit = 20) => {
  const params = new URLSearchParams({ q, kind, limit: String(limit) });
  const res = await fetch(`${API_BASE_URL}/master/search?${params.toString()}`, {
    headers: getAuthHeaders(),
  });
  if (res.status === 401) { handleUnauthorized(); return null; }
  let data = null;
  try { data = await res.json(); } catch { /* empty body */ }
  if (!res.ok) throw new Error(parseErrorDetail(data, res.status));
  return data;
};

// 機関アンマッチ (科研費の所属機関名のうち LDB に無いもの)。
// 本体の cron が送っている「機関アンマッチリスト」メールを置き換える。
const kikanUnmatchRequest = async (path, { method = 'GET', body } = {}) => {
  const res = await fetch(`${API_BASE_URL}/kikan_unmatch${path}`, {
    method,
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  if (!res.ok) {
    const t = await res.text().catch(() => '');
    let msg = `HTTP ${res.status}`;
    try { msg = JSON.parse(t).detail || msg; } catch (e) { msg = t || msg; }
    throw new Error(msg);
  }
  return res.json();
};

export const getKikanUnmatchQueue = ({ status = 'open', q } = {}) => {
  const p = new URLSearchParams({ status });
  if (q) p.set('q', q);
  return kikanUnmatchRequest(`/queue?${p.toString()}`);
};
export const ingestKikanUnmatch = () =>
  kikanUnmatchRequest('/ingest', { method: 'POST' });
export const getKikanUnmatchCandidates = (id) =>
  kikanUnmatchRequest(`/${id}/candidates`);
export const decideKikanUnmatch = (body) =>
  kikanUnmatchRequest('/decide', { method: 'POST', body });
export const undecideKikanUnmatch = (body) =>
  kikanUnmatchRequest('/undecide', { method: 'POST', body });
export const writebackKikanUnmatch = (dryRun = true) =>
  kikanUnmatchRequest(`/writeback?dry_run=${dryRun}`, { method: 'POST' });

// ── アンマッチ調査 (調査担当 / リモートワーカー) ───────────────────────────
// 2026-10-26: アンマッチの作業を 2 段に分けた。
//   ① 「正常に紐付き」かつ確信度 100% → 主任担当者が一覧でまとめて仮決定
//   ② それ以外 → 調査担当が調べて**所見つきで報告**し、主任担当者が仮決定する
export const getXrefResearchQueue = (params = {}) => {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') q.append(k, v);
  });
  return xrefRequest(`/research/queue?${q.toString()}`);
};

export const getXrefResearchStats = () => xrefRequest('/research/stats');

export const claimXrefResearch = (queueId) =>
  xrefRequest('/research/claim', { method: 'POST', body: { queue_id: queueId } });

export const releaseXrefResearch = (queueId) =>
  xrefRequest('/research/release', { method: 'POST', body: { queue_id: queueId } });

export const reportXrefResearch = (payload) =>
  xrefRequest('/research/report', { method: 'POST', body: payload });

export const returnXrefResearch = (queueId, note) =>
  xrefRequest('/research/return', { method: 'POST', body: { queue_id: queueId, note } });

// いまログインしている人の役割。トークンの claim ではなく allowed_users の行を見る
// (役割を変えてもログインし直さずに効く)。
export const getMe = async () => {
  const res = await fetch(`${API_BASE_URL}/auth/me`, { headers: getAuthHeaders() });
  if (res.status === 401) { handleUnauthorized(); return null; }
  if (!res.ok) return null;
  try { return await res.json(); } catch { return null; }
};

// 調査担当の月別の作業件数 (報酬の根拠。主任担当者だけ)
export const getXrefResearchMonthly = (months = 12) =>
  xrefRequest(`/research/monthly?months=${months}`);

// 主任担当者が報告を採って仮決定する (検収)。機関の登録案が付いていれば、ここで登録される
export const acceptXrefResearch = (queueId) =>
  xrefRequest('/research/accept', { method: 'POST', body: { queue_id: queueId } });
